/**
 * Generic keyboard shortcuts hook.
 * Can be used by any product to bind shortcuts to handlers with optional scope management.
 */

import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useMemo, type RefObject } from "react";
import {
  getEventShortcutKey,
  getShortcutKey,
  shouldHandleKeyboardEvent,
  type KeyboardShortcut,
} from "./keyboard-shortcut";

export interface ShortcutBinding {
  shortcut: KeyboardShortcut;
  handler: () => void;
}

export interface UseKeyboardShortcutsOptions<TScope extends string = string> {
  /** Array of shortcut-to-handler bindings */
  bindings: readonly ShortcutBinding[];
  /** The scope ID for these shortcuts (used for shortcut key matching) */
  scope: TScope;
  /** Whether shortcuts are enabled (default: true) */
  enabled?: boolean;
  /**
   * If provided, enables Escape handling:
   * - Calls popScope (if provided) then onEscape when Escape is pressed
   */
  onEscape?: () => void;
  /**
   * Optional scope management callbacks.
   * If provided along with onEscape, pushScope is called on mount
   * and popScope is called on unmount.
   * Note: pushScope takes no parameters - capture the scope in a closure.
   */
  scopeManagement?: { pushScope: () => void; popScope: () => void };
  /**
   * Optional ref to a container element. When provided, shortcuts only fire
   * if the event target is inside this container. Use this to isolate
   * multiple instances of the same shortcut bindings to their own DOM subtree.
   */
  containerRef?: RefObject<HTMLElement | null>;
}

/**
 * Generic hook for binding keyboard shortcuts to handlers.
 * Works with any scope type and optional scope stack management.
 *
 * @example
 * // Simple usage without scope management
 * useKeyboardShortcuts({
 *   bindings: [
 *     { shortcut: { code: "KeyJ", keyLabel: "J" }, handler: navigateDown },
 *     { shortcut: { code: "KeyK", keyLabel: "K" }, handler: navigateUp },
 *   ],
 *   scope: "table",
 * });
 *
 * @example
 * // With scope management (for submenus)
 * useKeyboardShortcuts({
 *   bindings: priorityOptions.map(opt => ({
 *     shortcut: opt.shortcut,
 *     handler: () => onSelect(opt.value),
 *   })),
 *   scope: "priority",
 *   onEscape: onClose,
 *   scopeManagement: { pushScope, popScope },
 * });
 */
export function useKeyboardShortcuts<TScope extends string = string>({
  bindings,
  scope,
  enabled = true,
  onEscape,
  scopeManagement,
  containerRef,
}: UseKeyboardShortcutsOptions<TScope>): void {
  const shortcutMap = useMemo(() => {
    const map = new Map<
      string,
      { handler: () => void; shortcut: KeyboardShortcut }
    >();
    for (const { shortcut, handler } of bindings) {
      map.set(getShortcutKey(shortcut, scope), { handler, shortcut });
    }
    return map;
  }, [bindings, scope]);

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (
      containerRef &&
      (!containerRef.current ||
        !(event.target instanceof Node) ||
        !containerRef.current.contains(event.target))
    ) {
      return;
    }

    if (onEscape && event.key === "Escape") {
      // Escape should always work (doesn't need shouldHandleKeyboardEvent check)
      event.preventDefault();
      scopeManagement?.popScope();
      onEscape();
      return;
    }

    const eventKey = getEventShortcutKey(event, scope);
    const binding = shortcutMap.get(eventKey);

    if (binding) {
      // Check if event should be handled for this specific shortcut
      if (!shouldHandleKeyboardEvent(event, binding.shortcut)) {
        return;
      }

      event.preventDefault();
      binding.handler();
    }
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Push scope on mount if scope management is enabled and we have onEscape
    if (onEscape && scopeManagement) {
      scopeManagement.pushScope();
    }

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      // Pop scope on unmount if we pushed it
      if (onEscape && scopeManagement) {
        scopeManagement.popScope();
      }
    };
  }, [enabled, onEscape, scope, scopeManagement, handleKeyDown]);
}
